import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { Row, Col } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { Link } from 'react-router-dom'
import getDc3AttestationAssigned from 'api/administration/courses/GetDc3AttestationAssigned'

import { IMainState } from 'store/states'
import * as fromActions from 'store/actions/courses.action'
import Img from 'components/ui/Img'
import defaultCourseBanner from 'assets/defaultCourse.png'
import Breadcrumb, { BreadcrumbItem } from 'design-system/breadcrumb'
import { dateParse } from 'helpers/date.helper'
import { CourseTypeDic } from 'enum/course-type.enum'
import { SynchronousCourseTypes } from 'enum/synchronousCourseTypes.enum'
import { MonitoringCourseProps, PathRoutes } from 'enum/routes'
import Loader from 'components/ui/loader/Loader'
import { useTranslationRankmi } from 'helpers/translations.helper'

import { ExternalCourseIndicator } from 'components/ui/CourseCards/components/molecules/externalCourseIndicator'
import Wrapper from 'components/reusables/wrapper'
import ProfilesCountCell from 'components/reusables/profilesCountCell'
import { CourseStateType } from 'enum/course-state.enum'
import { LegalAttestationSection, Statistics } from './components'
import { InactiveCourseMonitoringAlert } from './components/InactiveCourseMonitoringAlert'
import { prefix } from './constants'
import styles from './Course.module.scss'

export const Course: React.FC = () => {
  const { t } = useTranslation()
  const tRelative = useTranslationRankmi({
    prefix,
  })
  const dispatch = useDispatch()
  const { id = 0 } = useParams<MonitoringCourseProps>()

  const { course, loading } = useSelector(({ courses }: IMainState) => courses)
  const attestationsEnabled = useSelector(
    ({ general }: IMainState) => general.permissions.attestationsEnabled
  )
  const lessons = course?.lessons || []
  const [legalAttestation, setLegalAttestation] = useState({
    visible: false,
    assigned: false,
    attestationId: null as number | null,
  })

  useEffect(() => {
    dispatch({ type: fromActions.GET_COURSE, payload: id })
  }, [dispatch, id])

  useEffect(() => {
    const numericCourseId = Number(id)

    if (!attestationsEnabled || !numericCourseId) {
      setLegalAttestation({
        visible: false,
        assigned: false,
        attestationId: null,
      })
      return
    }

    let cancelled = false

    getDc3AttestationAssigned(numericCourseId)
      .then(({ normative, assigned, attestationId }) => {
        if (!cancelled) {
          setLegalAttestation({
            visible: normative,
            assigned,
            attestationId: attestationId ?? null,
          })
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLegalAttestation({
            visible: false,
            assigned: false,
            attestationId: null,
          })
        }
      })

    return () => {
      cancelled = true
    }
  }, [attestationsEnabled, id])

  const prefixMetadata = `${prefix}.metadata`
  const isIntegrationCourse = course['integrationCourseLink']

  const translateCourseType = CourseTypeDic[Number(course && course.courseType)]
  const isSynchronous = !!course.synchronousCourse
  const { synchronousCourseType } = {
    ...course.synchronousCourse,
  }
  const type = React.useMemo(() => {
    const labels = []
    if (translateCourseType)
      labels.push(t(`commons.course.type.${translateCourseType}`))
    if (isSynchronous) {
      const text =
        synchronousCourseType && SynchronousCourseTypes[synchronousCourseType]
      if (text) labels.push(t(`commons.course.type.synchronousTypes.${text}`))
    }
    return labels.join(' - ')
  }, [isSynchronous, synchronousCourseType, t, translateCourseType])

  const isInactive = Number(course?.state) === CourseStateType.Inactive

  const displayHoursCourse = () => {
    if (isIntegrationCourse && (course.hours === 0 || course.hours === null)) {
      return '-'
    }

    return tRelative(`data.hours`, {
      count: course.hours || 0,
    })
  }

  return (
    <Wrapper shadow={true}>
      {(loading && (
        <div className="d-flex justify-content-center">
          <Loader color />
        </div>
      )) || (
        <Row style={{ marginTop: -11 }}>
          <Col>
            <Breadcrumb separator=">">
              <Link
                component={(props: any) => (
                  <BreadcrumbItem
                    text={t('monitoring.course.breadcrumb')}
                    onClick={(e) => {
                      e.preventDefault()
                      props?.navigate()
                    }}
                    href={props.href}
                  />
                )}
                to={PathRoutes.MONITORING_COURSES}
              />
              <BreadcrumbItem text={course.name} />
            </Breadcrumb>
            <div className={styles.courseHeader}>
              <div className={styles.courseHeaderMain}>
                <h1 className={styles.courseTitle}>{course.name}</h1>
                <div className={styles.courseMetadataGrid}>
                  <div
                    className={classNames(styles.courseMetadataRow, {
                      [styles.courseMetadataRowWithLegalSlot]:
                        attestationsEnabled && legalAttestation.visible,
                    })}
                  >
                    <div className={styles.courseDataColumn}>
                      <p className={styles.courseDataTitle}>
                        {t(`${prefixMetadata}.labels.accessType`)}
                      </p>
                      <div className={classNames(styles.courseData)}>
                        {type}
                      </div>
                    </div>
                    <div className={styles.courseDataColumn}>
                      <p className={styles.courseDataTitle}>
                        {t(`${prefixMetadata}.labels.startDate`)}
                      </p>
                      <p className={styles.courseData}>
                        {t(dateParse(course.startDate))}
                      </p>
                    </div>
                    <div className={styles.courseDataColumn}>
                      <p className={styles.courseDataTitle}>
                        {t(`${prefixMetadata}.labels.untilDate`)}
                      </p>
                      <p className={styles.courseData}>
                        {t(dateParse(course.untilDate))}
                      </p>
                    </div>
                    <div className={styles.courseDataColumn}>
                      <p className={styles.courseDataTitle}>
                        {t(`${prefixMetadata}.labels.optionalProfiles`)}
                      </p>
                      <div className={styles.courseData}>
                        <ProfilesCountCell
                          resourceId={course.id}
                          resourceType="course"
                          kind="optional"
                          count={course.optionalProfiles?.length || 0}
                          translationKey={`${prefixMetadata}.profilesCount`}
                        />
                      </div>
                    </div>
                    <div className={styles.courseDataColumn}>
                      <p className={styles.courseDataTitle}>
                        {t(`${prefixMetadata}.labels.requiredProfiles`)}
                      </p>
                      <div className={styles.courseData}>
                        <ProfilesCountCell
                          resourceId={course.id}
                          resourceType="course"
                          kind="required"
                          count={course.requiredProfiles?.length || 0}
                          translationKey={`${prefixMetadata}.profilesCount`}
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    className={classNames(styles.courseMetadataRow, {
                      [styles.courseMetadataRowWithLegalSlot]:
                        attestationsEnabled && legalAttestation.visible,
                    })}
                  >
                    <div className={styles.courseDataColumn}>
                      <p className={styles.courseDataTitle}>
                        {t(`${prefixMetadata}.labels.lessons`)}
                      </p>
                      <p className={styles.courseData}>
                        {isIntegrationCourse
                          ? '-'
                          : tRelative(`data.lessons`, {
                              count: lessons.length || 0,
                            })}
                      </p>
                    </div>
                    <div className={styles.courseDataColumn}>
                      <p className={styles.courseDataTitle}>
                        {t(`${prefixMetadata}.labels.tasks`)}
                      </p>
                      <p className={styles.courseData}>
                        {isIntegrationCourse
                          ? '-'
                          : tRelative(`data.tasks`, {
                              count: course.tasksCount,
                            })}
                      </p>
                    </div>
                    <div className={styles.courseDataColumn}>
                      <p className={styles.courseDataTitle}>
                        {t(`${prefixMetadata}.labels.surveys`)}
                      </p>
                      <p className={styles.courseData}>
                        {isIntegrationCourse
                          ? '-'
                          : tRelative(`data.surveys`, {
                              count: course.surveysCount,
                            })}
                      </p>
                    </div>
                    <div className={styles.courseDataColumn}>
                      <p className={styles.courseDataTitle}>
                        {t(`${prefixMetadata}.labels.evaluations`)}
                      </p>
                      <p className={styles.courseData}>
                        {isIntegrationCourse
                          ? '-'
                          : tRelative(`data.evaluations`, {
                              count: course.evaluationsCount,
                            })}
                      </p>
                    </div>
                    <div className={styles.courseDataColumn}>
                      <p className={styles.courseDataTitle}>
                        {t(`${prefixMetadata}.labels.hours`)}
                      </p>
                      <p className={styles.courseData}>
                        {displayHoursCourse()}
                      </p>
                    </div>
                    {attestationsEnabled && legalAttestation.visible ? (
                      <div className={styles.courseDataColumn}>
                        <p className={styles.courseDataTitle}>
                          {t(`${prefix}.legalAttestation.label`)}
                        </p>
                        <div className={styles.courseData}>
                          <LegalAttestationSection
                            inline
                            assigned={legalAttestation.assigned}
                            attestationId={legalAttestation.attestationId}
                            courseName={course.name}
                            courseHours={course.hours}
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className={styles.courseBanner}>
                <Img
                  className={styles.courseBannerImage}
                  src={course.bannerUrl || defaultCourseBanner}
                  alt=""
                  imageHandlerProps={{ height: 200 }}
                  onError={(event) => {
                    event.currentTarget.src = defaultCourseBanner
                  }}
                />
                <ExternalCourseIndicator
                  origin="MONITOR_COURSE_DETAIL"
                  url={course['integrationCourseLink']}
                />
              </div>
            </div>
          </Col>
        </Row>
      )}
      {!loading && isInactive ? (
        <Row className={styles.inactiveCourseAlertRow}>
          <Col xs={12}>
            <InactiveCourseMonitoringAlert />
          </Col>
        </Row>
      ) : null}
      <Row>
        <Col md={12}>
          <h1 className={styles.statisticsTitle}>
            {t('monitoring.course.statistics.title')}
          </h1>
        </Col>
        <Col xs={12}>
          <Statistics course={id} isSynchronous={isSynchronous} />
        </Col>
      </Row>
    </Wrapper>
  )
}

export default Course
